import React, { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BoxRes } from 'types/Box';

import { DATE_AND_TIME_FORMAT_SHORT } from '../AppConstants';
import { isAxiosErrorWithStatus } from '../helpers/AuthHelpers';
import { formatDateTime, removeUnderscoreToUppercase } from '../helpers/FormattingHelpers';
import { getScanStatusClassName } from '../helpers/ScanningHelpers';
import { useGetImportStatus } from '../services/hooks/useGetImportStatus';
import { ScanRes } from '../types/Scan';
import ErrorHandler from './ErrorHandler';
import { TOAST_TYPE_MAP, ToastCustom } from './ToastCustom';

import '../styles/components/BoxListItem.less';

interface Props {
    box: BoxRes;
    scan: ScanRes;
    deleteBox: (boxId: number) => void;
    isDeleting: boolean;
    scanBelongsToUserLocation: boolean;
}

const BoxListItem = ({ scan, box, deleteBox, isDeleting, scanBelongsToUserLocation }: Props): ReactElement => {
    const navigate = useNavigate();

    // This hook will make a single call to API, to check the status of the box, and only if the box is in progress.
    // If the status is PENDING or PROCESSING, it will start polling for the status.
    const { importJobStatus, error: pollingJobStatusErr } = useGetImportStatus({
        scanId: scan.id,
        boxId: box.id,
        shouldStartOnMount: box.statusName === 'IN_PROGRESS',
        onError: ErrorHandler,
        onTimeout: () => ToastCustom('The import job timed out!', TOAST_TYPE_MAP.ERROR)
    });

    const isImportOngoing = importJobStatus === 'PENDING' || importJobStatus === 'PROCESSING';

    // We don't want to display err icon if 400, since this will always be the case for all boxes that do not have an import job
    const isNot400Err = !isAxiosErrorWithStatus(pollingJobStatusErr, 400);

    const getStatusIcon = (scanStatus: string): ReactElement => {
        let statusIcon = (
            <FontAwesomeIcon data-testid="question-circle" icon={['far', 'question-circle']} size="2x" id="gray" />
        );

        if (scanStatus === 'IN_PROGRESS' && isImportOngoing) {
            statusIcon = <FontAwesomeIcon data-testid="spinner-icon" icon="spinner" spin={true} size="xl" id="gray" />;
        } else if (scanStatus === 'IN_PROGRESS' && importJobStatus === 'FAILED' && isNot400Err) {
            statusIcon = <FontAwesomeIcon data-testid="times-icon" icon={['far', 'times-circle']} size="2x" id="red" />;
        } else if (scanStatus === 'IN_PROGRESS') {
            statusIcon = (
                <FontAwesomeIcon data-testid="excl-icon" icon={['far', 'exclamation-circle']} size="2x" id="blue" />
            );
        } else if (scanStatus === 'COMPLETE') {
            statusIcon = (
                <FontAwesomeIcon data-testid="check-icon" icon={['far', 'check-circle']} size="2x" id="green" />
            );
        }
        return statusIcon;
    };

    return (
        <div className="box-item flex-space-between">
            <ReactTooltip id="delete-tip" type="dark" effect="solid" place="left" />
            <div
                onClick={() => {
                    navigate(`/scanning/box-scan/${scan.id}/box/${box.id}`);
                }}
                role="navigation"
                className="nav-container"
            >
                <div className="col-1">{getStatusIcon(box.statusName)}</div>
                <div className="col-2">
                    <div>
                        <strong>
                            Box ID {box.id} - {box.loggerCount} {box.loggerCount === 1 ? 'logger' : 'loggers'}
                        </strong>
                    </div>
                    <div>
                        <div className={`status ${getScanStatusClassName(box.statusName)}`}>
                            {removeUnderscoreToUppercase(box.statusName)}
                        </div>
                    </div>
                </div>
                <div className="col-3">
                    <div data-testid="user-box-name">
                        <FontAwesomeIcon
                            data-testid="user-icon"
                            icon="user"
                            className="margin-right"
                            fixedWidth={true}
                        />
                        <span>Box by: </span>
                        {box.createdByUsername}
                    </div>
                    <div data-testid="creation-date">
                        <FontAwesomeIcon
                            data-testid="created-icon"
                            icon={['far', 'clock']}
                            className="margin-right"
                            fixedWidth={true}
                        />
                        <span>Created: </span>
                        {formatDateTime(box.createdTime, DATE_AND_TIME_FORMAT_SHORT)}
                    </div>
                </div>
            </div>
            {box.statusName !== 'COMPLETE' && (
                <div className="btn-container">
                    <button
                        data-testid="delete-button"
                        data-for="delete-tip"
                        data-tip={!scanBelongsToUserLocation ? 'Not available on a remote location' : ''}
                        disabled={isDeleting || !scanBelongsToUserLocation}
                        title="Delete Box"
                        className="no-style"
                        onClick={() => deleteBox(box.id)}
                    >
                        {isDeleting ? (
                            <FontAwesomeIcon
                                data-testid="delete-spin-icon"
                                icon="spinner"
                                spin={true}
                                size="lg"
                                role="tab"
                            />
                        ) : (
                            <FontAwesomeIcon icon="trash" size="lg" />
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default BoxListItem;
